import React from 'react';
import { motion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Shield, Lock, Eye, Users, FileText, Mail, Bot } from 'lucide-react';
import { AnimatedBackground } from '../AnimatedBackground';

export function Privacy() {
  const sections = [
    {
      icon: <FileText className="text-[#00BFFF]" size={32} />,
      title: 'Information We Collect',
      content: [
        'EdgeAI Academy, operated by MindEngage Inc., collects limited information necessary to provide our training and collaboration services:',
        '• Account Information: Name, email address, password, organization or affiliation (if applicable), role, and areas of interest in Edge AI, embedded systems, and IoT.',
        '• Usage Data: Course enrollments, lesson progress, quiz results, lab activity, and learning analytics to personalize and improve the learning experience.',
        '• AI Chat Interactions: Questions and conversations with our AI Study Assistant (including message content, metadata, and timestamps). These interactions may be stored for a limited time to improve quality, safety, and debugging.',
        '• Study Group & Messenger Data: Messages sent in study groups, channels, and direct chats; membership in groups; reactions; and timestamps. This includes any files or links you choose to share.',
        '• Technical Data: Browser type, IP address, device identifiers, approximate location (derived from IP), operating system, and related telemetry for security, diagnostics, and performance optimization.',
        '• Communications: Messages sent through contact forms, support tickets, or email, including any information you choose to provide.',
      ],
    },
    {
      icon: <Lock className="text-[#FFD93D]" size={32} />,
      title: 'How We Use Your Information',
      content: [
        'We use collected information to operate, secure, and improve EdgeAI Academy:',
        '• Delivering and personalizing courses, labs, and training content.',
        '• Enabling collaboration features such as study groups, messaging, and shared resources.',
        '• Providing AI-powered assistance (e.g., explanations, code help, troubleshooting guidance).',
        '• Communicating with you about account activity, course updates, new features, and service-related announcements.',
        '• Monitoring for abuse, fraud, and security incidents, and enforcing our acceptable-use guidelines.',
        '• Analyzing anonymized or aggregated usage data to improve our curriculum, platform performance, and user experience.',
        '• Complying with legal obligations and protecting the rights, property, and safety of users and MindEngage Inc.',
      ],
    },
    {
      icon: <Users className="text-[#FF6F61]" size={32} />,
      title: "Who EdgeAI Academy Is For & Children's Privacy",
      content: [
        'EdgeAI Academy is designed primarily for adult learners, university students, and professionals working with Edge AI, embedded systems, and IoT.',
        '• The platform is not directed to children and is not intended for learners under the age of 16.',
        '• We do not knowingly collect personal information from children under 16. If you are under 16, please do not create an account or submit personal information.',
        '• If we learn that we have collected personal information from a child without appropriate consent, we will take steps to delete that information.',
        '• Parents or guardians who believe their child may have provided us information can contact us using the details at the end of this policy.',
      ],
    },
    {
      icon: <Bot className="text-[#00BFFF]" size={32} />,
      title: 'AI Study Assistant & Chatbot',
      content: [
        'EdgeAI Academy includes AI-powered features to support learning and experimentation:',
        '• Purpose: The AI Study Assistant helps with explanations, code and configuration guidance, debugging suggestions, and conceptual questions related to Edge AI, embedded systems, and related topics.',
        '• Data Collection: When you interact with the AI, we process and may temporarily store your questions, the AI’s responses, and metadata (such as timestamps and context) to deliver responses and maintain quality.',
        '• Third-Party AI Service: Chat messages are processed through OpenAI’s API (for example, GPT-4-class models) to generate responses.',
        "• Safety & Scope: The assistant is designed to decline clearly harmful or out-of-scope requests (e.g., unsafe instructions, harassment, or non-technical personal advice). It should not be used as a substitute for professional legal, medical, financial, or safety-critical advice.",
        '• Limited Retention: We may retain AI interaction logs for a limited period to improve reliability, safety, and debugging. Where possible, logs are decoupled from directly identifying information.',
        '• OpenAI Data Processing: Chat data sent to OpenAI is subject to OpenAI’s data usage policies for API customers. OpenAI does not use API data sent via the API to train or improve their models.',
      ],
    },
    {
      icon: <Users className="text-[#FF6F61]" size={32} />,
      title: 'Study Group Chat & Collaboration Features',
      content: [
        'EdgeAI Academy provides real-time study groups and messaging features to support collaborative learning:',
        '• Message Content: Messages, files, and links you share in study groups or direct messages are stored so that you and other authorized participants can view them over time.',
        '• Visibility: Your messages may be visible to other group members, course instructors, and authorized administrators from your organization (if your account is provisioned through an employer or institution).',
        '• Moderation & Safety: We may monitor or review message content (manually or using automated tools) to enforce community guidelines, investigate abuse, and protect user safety.',
        '• No Advertising Use: We do not sell or use your private messages for third-party advertising. Aggregated and anonymized insights may be used to improve learning experiences and platform features.',
        '• Your Responsibility: Please avoid sharing sensitive personal data, confidential company information, or secrets in group chats or public channels.',
      ],
    },
    {
      icon: <Shield className="text-[#8B5CF6]" size={32} />,
      title: 'Data Security',
      content: [
        'MindEngage Inc. implements industry-standard security practices to safeguard your information:',
        '• Encrypted data transmission using SSL/TLS.',
        '• Segregated and access-controlled production environments.',
        '• Role-based access controls, granting data access only to authorized personnel with a business need.',
        '• Regular backups and infrastructure monitoring to reduce the risk of data loss or downtime.',
        '• Security reviews and updates of third-party dependencies and services.',
        'However, no method of transmission or storage over the internet is completely secure. We encourage you to use strong, unique passwords and enable additional security features your organization may provide.',
      ],
    },
    {
      icon: <Eye className="text-[#00BFFF]" size={32} />,
      title: 'Organization Accounts & Data Sharing',
      content: [
        'EdgeAI Academy may be used directly by individuals or provided through organizations such as employers, universities, or training partners:',
        '• When you access EdgeAI through an organization, certain information (e.g., your name, email, course progress, and completion data) may be shared with that organization and its authorized administrators.',
        '• MindEngage provides the platform infrastructure and support. We access user data only as needed for operations, security, support, and product improvement.',
        '• We do not sell your personal information to third-party advertisers.',
        '• We may use de-identified and aggregated data to understand engagement trends and improve the platform.',
      ],
    },
    {
      icon: <FileText className="text-[#FFD93D]" size={32} />,
      title: 'Third-Party Services',
      content: [
        'Our platform integrates with select third-party services to deliver and support EdgeAI Academy:',
        '• OpenAI API: Powers AI Study Assistant features. Chat content and metadata are transmitted to OpenAI for processing. OpenAI does not use API data to train its models.',
        '• Analytics Providers: Help us understand high-level platform usage (e.g., page views, feature engagement) using aggregated and/or pseudonymized data.',
        '• Payment Processors: Securely handle subscription payments and billing information when applicable.',
        '• Infrastructure & Communication Tools: Cloud hosting, email delivery, file storage, and error monitoring providers that support reliable platform operations.',
        'All third-party services are reviewed for appropriate data-protection practices, and where required, we enter into data-processing agreements with these providers.',
      ],
    },
    {
      icon: <Users className="text-[#FF6F61]" size={32} />,
      title: 'Your Rights & Choices',
      content: [
        'Depending on your jurisdiction and how your account was provisioned, you may have certain rights regarding your information:',
        '• Access: Request confirmation that we process your personal data and receive a copy where appropriate.',
        '• Correction: Ask us to correct inaccurate or incomplete information.',
        '• Deletion: Request deletion of your personal data, subject to legal and contractual obligations (for example, where your organization controls the account).',
        '• Restriction & Objection: In some cases, request that we limit certain processing activities or object to specific uses.',
        '• Opt-Out of Marketing: Unsubscribe from non-essential marketing or promotional communications. Service and transactional messages may still be sent.',
        'To exercise these rights, contact us using the information provided at the end of this policy. If your access is provided through an organization, we may redirect your request to the appropriate administrator.',
      ],
    },
    {
      icon: <Lock className="text-[#8B5CF6]" size={32} />,
      title: 'Cookies & Tracking Technologies',
      content: [
        'We use cookies and similar technologies to operate and improve EdgeAI Academy:',
        '• Essential Cookies: Required for core functionality such as authentication, session management, and security.',
        '• Preferences: Used to remember your settings (e.g., theme, layout, or language where applicable).',
        '• Analytics Cookies: Help us understand how users interact with the platform so we can improve performance and usability.',
        'You can manage cookies through your browser settings. Disabling certain cookies may affect your ability to use some features.',
      ],
    },
    {
      icon: <Mail className="text-[#FFD93D]" size={32} />,
      title: 'Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time as our services, technology, and legal requirements evolve:',
        '• Material changes will be communicated via email or a prominent in-product notice where appropriate.',
        '• The “Last updated” date will be revised to reflect the most recent version.',
        '• Your continued use of EdgeAI Academy after changes become effective constitutes acceptance of the updated policy.',
        '• Last updated: November 19, 2025',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative text-slate-100">
      {/* Dimmed Background */}
      <AnimatedBackground variant="dimmed" />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00BFFF] to-[#8B5CF6] mb-6 shadow-[0_0_40px_rgba(0,191,255,0.6)]">
            <Shield className="text-white" size={40} />
          </div>
          <h1 className="text-5xl md:text-6xl text-white mb-4">Privacy Policy</h1>
          <p className="text-xl text-cyan-100/90 max-w-3xl mx-auto">
            Your privacy and the security of your learning data are our top priorities.
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <Card className="bg-[#050915]/95 backdrop-blur-xl border border-cyan-400/40 shadow-[0_0_40px_rgba(0,229,209,0.25)]">
            <CardContent className="pt-6 text-slate-100/90 space-y-4 text-sm md:text-base">
              <p>
                <strong>Effective Date:</strong> November 19, 2025
              </p>
              <p>
                Welcome to EdgeAI Academy, an advanced learning platform for Edge AI, embedded systems,
                and IoT, operated by MindEngage Inc. This Privacy Policy explains how we collect, use,
                protect, and share information when you use our courses, labs, AI assistants, and
                study-group features.
              </p>
              <p>
                We are committed to protecting your privacy and handling personal data responsibly, in
                line with applicable data-protection laws. By using EdgeAI Academy, you agree to the
                practices described in this policy. If you have questions or concerns, please contact us
                using the information at the end of this document.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Policy Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <Card className="bg-[#050915]/95 backdrop-blur-xl border border-cyan-400/20 hover:border-cyan-400/40 transition-colors shadow-[0_0_30px_rgba(0,0,0,0.6)]">
              <CardHeader>
                <CardTitle className="text-2xl text-cyan-100 flex items-center gap-3">
                  {section.icon}
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-200/90 space-y-3 text-sm md:text-base">
                {section.content.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className={paragraph.startsWith('•') ? 'ml-4' : ''}
                  >
                    {paragraph}
                  </p>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <Card className="bg-gradient-to-br from-[#00BFFF]/15 to-[#8B5CF6]/15 backdrop-blur-xl border border-cyan-400/60 shadow-[0_0_40px_rgba(0,191,255,0.4)]">
            <CardHeader>
              <CardTitle className="text-2xl text-cyan-100 flex items-center gap-3">
                <Mail className="text-[#00BFFF]" size={32} />
                Contact Us About Privacy
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-100/90 space-y-2 text-sm md:text-base">
              <p>
                If you have questions about this Privacy Policy, wish to exercise your data rights, or
                need to report a privacy concern, please contact us:
              </p>
              <div className="mt-4 space-y-1">
                <p>
                  <strong>MindEngage Inc</strong>
                </p>
                <p>84 W. Santa Clara St., #700</p>
                <p>San Jose, CA 95113</p>
                <p className="mt-3">
                  <strong>Phone:</strong>{' '}
                  <a
                    href="tel:+18669536888"
                    className="text-[#00BFFF] hover:underline"
                  >
                    1 866 953 6888
                  </a>
                </p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:privacy@mindengage.ai"
                    className="text-[#00BFFF] hover:underline"
                  >
                    privacy@mindengage.ai
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
